import type { CoachAdvice } from "@kikik/mahjong-coach";
import type { PlayerId } from "@kikik/mahjong-core";
import { useI18n } from "../i18n";
import type { Message } from "@kikik/i18n";
import { Drawer } from "./Drawer";

export interface CoachPanelProps {
  advice: CoachAdvice | null;
  situation?: Message | null;
  nameFor: (playerId: PlayerId) => string;
  onAsk?: () => void;
  onFollow?: () => void;
  /** Lesson-specific instruction, shown above the generated advice. */
  lessonHint?: string;
}

/**
 * The coach speaks: what to do, and why.
 *
 * It is a compact drawer beside the table rather than a permanent column, so
 * it can be folded away without losing anything: the lesson instruction, the
 * suggestion, the reason behind it and the "do this" button are all still one
 * click away, and none of them ever sits on top of the player's hand.
 *
 * Advice arrives as language-neutral descriptors, so the panel is a pure
 * rendering of engine state; it never mutates the game.
 */
export function CoachPanel({
  advice,
  situation,
  nameFor,
  onAsk,
  onFollow,
  lessonHint,
}: CoachPanelProps) {
  const { t, tm } = useI18n();
  const options = { playerName: nameFor };

  return (
    <Drawer className="panel coach" title={t("coach.title")} defaultOpen testId="coach-panel">
      {lessonHint ? (
        <p className="coach__lesson">
          <span className="coach__label">{t("tutorial.coachSays")}:</span> {lessonHint}
        </p>
      ) : null}
      {situation ? <p className="coach__situation">{tm(situation, options)}</p> : null}

      {advice ? (
        <div className="coach__advice">
          <p className="coach__headline">
            <span className="coach__label">{t("coach.label.hint")}:</span>{" "}
            {tm(advice.headline, options)}
          </p>
          <p className="coach__reason">
            <span className="coach__label">{t("coach.label.reason")}:</span>{" "}
            {tm(advice.reason, options)}
          </p>
          {advice.notes.length > 0 ? (
            <ul className="coach__notes">
              {advice.notes.map((note, index) => (
                <li key={index}>
                  <span className="coach__label">
                    {note.kind === "keep" ? t("coach.label.keep") : t("coach.label.consider")}:
                  </span>{" "}
                  {tm(note.message, options)}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : (
        <p className="muted">{t("coach.empty")}</p>
      )}

      <div className="coach__actions">
        {onFollow && advice?.action ? (
          <button type="button" className="button button--primary" onClick={onFollow}>
            {t("coach.follow")}
          </button>
        ) : null}
        {onAsk ? (
          <button type="button" className="button button--ghost" onClick={onAsk}>
            {t("coach.ask")}
          </button>
        ) : null}
      </div>
    </Drawer>
  );
}
